import { createTheme } from "@mui/material/styles";

const theme = createTheme({
  palette: {
    mode: 'light',
    primary: {
      main: '#C6A75E', // Rich Gold
      light: '#DBC28C',
      dark: '#B08C4A',
      contrastText: '#FFFFFF',
    },
    secondary: {
      main: '#4A4A4A', // Medium Grey
      light: '#6B6B6B',
      dark: '#2D2D2D',
      contrastText: '#FFFFFF',
    },
    grey: {
      50: '#FAFAFA',
      100: '#F5F5F5',
      200: '#EEEEEE',
      300: '#E0E0E0',
      400: '#BDBDBD',
      500: '#9E9E9E',
      600: '#757575',
      700: '#616161',
      800: '#424242',
      900: '#212121',
    },
    background: {
      default: '#F5F5F5', // Light Grey
      paper: '#FFFFFF',
      elevated: '#FAFAFA',
    },
    text: {
      primary: '#2D2D2D', // Dark Grey
      secondary: '#616161', // Medium Grey
      disabled: '#9E9E9E',
      hint: '#C6A75E',
    },
    divider: 'rgba(158, 158, 158, 0.2)',
    action: {
      hover: 'rgba(198, 167, 94, 0.04)',
      selected: 'rgba(198, 167, 94, 0.08)',
      disabled: 'rgba(0, 0, 0, 0.26)',
      disabledBackground: 'rgba(0, 0, 0, 0.12)',
    },
  },
  typography: {
    fontFamily: '"Cormorant Garamond", "Times New Roman", serif',
    h1: {
      fontSize: '4.5rem',
      fontWeight: 600,
      letterSpacing: '-0.02em',
      lineHeight: 1.1,
      color: '#2D2D2D',
      '@media (max-width:600px)': {
        fontSize: '3rem',
      },
    },
    h2: {
      fontSize: '3.5rem',
      fontWeight: 600,
      letterSpacing: '-0.01em',
      color: '#2D2D2D',
      '@media (max-width:600px)': {
        fontSize: '2.5rem',
      },
    },
    h3: {
      fontSize: '2.5rem',
      fontWeight: 500,
      color: '#2D2D2D',
      '@media (max-width:600px)': {
        fontSize: '2rem',
      },
    },
    h4: {
      fontSize: '2rem',
      fontWeight: 500,
      letterSpacing: '0.02em',
      color: '#2D2D2D',
      '@media (max-width:600px)': {
        fontSize: '1.5rem',
      },
    },
    h5: {
      fontSize: '1.5rem',
      fontWeight: 500,
      color: '#2D2D2D',
    },
    h6: {
      fontSize: '1.25rem',
      fontWeight: 600,
      letterSpacing: '0.05em',
      textTransform: 'uppercase',
      color: '#C6A75E',
    },
    subtitle1: {
      fontFamily: '"Inter", "Helvetica", "Arial", sans-serif',
      fontSize: '1.1rem',
      fontWeight: 400,
      color: '#616161',
    },
    subtitle2: {
      fontFamily: '"Inter", "Helvetica", "Arial", sans-serif',
      fontSize: '0.9rem',
      fontWeight: 500,
      letterSpacing: '0.05em',
      textTransform: 'uppercase',
      color: '#C6A75E',
    },
    body1: {
      fontFamily: '"Inter", "Helvetica", "Arial", sans-serif',
      fontSize: '1.1rem',
      lineHeight: 1.8,
      fontWeight: 300,
      color: '#616161',
    },
    body2: {
      fontFamily: '"Inter", "Helvetica", "Arial", sans-serif',
      fontSize: '1rem',
      lineHeight: 1.6,
      fontWeight: 300,
      color: '#616161',
    },
    button: {
      fontFamily: '"Inter", "Helvetica", "Arial", sans-serif',
      fontWeight: 600,
      letterSpacing: '0.1em',
      textTransform: 'uppercase',
      fontSize: '0.85rem',
    },
    caption: {
      fontFamily: '"Inter", "Helvetica", "Arial", sans-serif',
      fontSize: '0.75rem',
      letterSpacing: '0.05em',
      color: '#9E9E9E',
    },
  },
  shape: {
    borderRadius: 0,
  },
  shadows: [
    'none',
    '0 2px 4px rgba(0,0,0,0.02)',
    '0 4px 8px rgba(0,0,0,0.03)',
    '0 8px 16px rgba(0,0,0,0.04)',
    '0 12px 24px rgba(0,0,0,0.04)',
    '0 16px 32px rgba(0,0,0,0.05)',
    '0 20px 40px rgba(0,0,0,0.05)',
    '0 24px 48px rgba(0,0,0,0.06)',
    '0 28px 56px rgba(0,0,0,0.06)',
    '0 32px 64px rgba(0,0,0,0.07)',
    '0 36px 72px rgba(0,0,0,0.07)',
    '0 40px 80px rgba(0,0,0,0.08)',
    '0 44px 88px rgba(0,0,0,0.08)',
    '0 48px 96px rgba(0,0,0,0.09)',
    '0 52px 104px rgba(0,0,0,0.09)',
    '0 56px 112px rgba(0,0,0,0.10)',
    '0 60px 120px rgba(0,0,0,0.10)',
    '0 64px 128px rgba(0,0,0,0.11)',
    '0 68px 136px rgba(0,0,0,0.11)',
    '0 72px 144px rgba(0,0,0,0.12)',
    '0 76px 152px rgba(0,0,0,0.12)',
    '0 80px 160px rgba(0,0,0,0.13)',
    '0 84px 168px rgba(0,0,0,0.13)',
    '0 88px 176px rgba(0,0,0,0.14)',
    '0 92px 184px rgba(0,0,0,0.14)',
  ],
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          padding: '12px 32px',
          fontSize: '0.9rem',
          transition: 'all 0.3s ease',
          position: 'relative',
          overflow: 'hidden',
          '&::after': {
            content: '""',
            position: 'absolute',
            bottom: 0,
            left: 0,
            width: '100%',
            height: '2px',
            backgroundColor: 'primary.main',
            transform: 'scaleX(0)',
            transformOrigin: 'right',
            transition: 'transform 0.3s ease',
          },
          '&:hover::after': {
            transform: 'scaleX(1)',
            transformOrigin: 'left',
          },
        },
        contained: {
          backgroundColor: 'primary.main',
          color: '#FFFFFF',
          boxShadow: '0 4px 12px rgba(198, 167, 94, 0.2)',
          '&:hover': {
            backgroundColor: 'primary.dark',
            transform: 'translateY(-2px)',
            boxShadow: '0 8px 24px rgba(198, 167, 94, 0.3)',
          },
        },
        containedSecondary: {
          backgroundColor: '#4A4A4A',
          color: '#FFFFFF',
          boxShadow: '0 4px 12px rgba(74, 74, 74, 0.2)',
          '&:hover': {
            backgroundColor: '#2D2D2D',
            transform: 'translateY(-2px)',
            boxShadow: '0 8px 24px rgba(74, 74, 74, 0.3)',
          },
        },
        outlined: {
          borderWidth: '2px',
          borderColor: 'primary.main',
          color: 'primary.main',
          backgroundColor: 'transparent',
          '&:hover': {
            borderWidth: '2px',
            borderColor: 'primary.dark',
            backgroundColor: 'rgba(198, 167, 94, 0.04)',
            transform: 'translateY(-2px)',
          },
        },
        outlinedSecondary: {
          borderWidth: '2px',
          borderColor: '#4A4A4A',
          color: '#4A4A4A',
          '&:hover': {
            borderWidth: '2px',
            borderColor: '#2D2D2D',
            backgroundColor: 'rgba(74, 74, 74, 0.04)',
            transform: 'translateY(-2px)',
          },
        },
        text: {
          color: 'primary.main',
          '&:hover': {
            backgroundColor: 'rgba(198, 167, 94, 0.04)',
          },
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          backgroundColor: '#FFFFFF',
          border: '1px solid rgba(158, 158, 158, 0.15)',
          transition: 'all 0.3s ease',
          boxShadow: '0 4px 12px rgba(0,0,0,0.02)',
          '&:hover': {
            borderColor: 'primary.main',
            transform: 'translateY(-4px)',
            boxShadow: '0 12px 32px rgba(0,0,0,0.05)',
          },
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          backgroundImage: 'none',
        },
        elevation1: {
          boxShadow: '0 2px 8px rgba(0,0,0,0.02)',
        },
        elevation2: {
          boxShadow: '0 4px 12px rgba(0,0,0,0.03)',
        },
        elevation3: {
          boxShadow: '0 8px 24px rgba(0,0,0,0.04)',
        },
        elevation4: {
          boxShadow: '0 12px 32px rgba(0,0,0,0.04)',
        },
      },
    },
    MuiAppBar: {
      styleOverrides: {
        root: {
          backgroundColor: 'rgba(255, 255, 255, 0.98)',
          backdropFilter: 'blur(10px)',
          borderBottom: '1px solid rgba(158, 158, 158, 0.2)',
          boxShadow: '0 2px 8px rgba(0,0,0,0.02)',
        },
        colorPrimary: {
          backgroundColor: 'rgba(255, 255, 255, 0.98)',
        },
      },
    },
    MuiDrawer: {
      styleOverrides: {
        paper: {
          backgroundColor: '#FFFFFF',
          borderRight: '1px solid rgba(158, 158, 158, 0.15)',
        },
      },
    },
    MuiDivider: {
      styleOverrides: {
        root: {
          borderColor: 'rgba(158, 158, 158, 0.15)',
        },
      },
    },
    MuiTextField: {
      styleOverrides: {
        root: {
          '& .MuiOutlinedInput-root': {
            backgroundColor: '#FFFFFF',
            '& fieldset': {
              borderColor: 'rgba(158, 158, 158, 0.3)',
              borderWidth: '1px',
            },
            '&:hover fieldset': {
              borderColor: 'primary.main',
            },
            '&.Mui-focused fieldset': {
              borderColor: 'primary.main',
              borderWidth: '2px',
            },
          },
          '& .MuiInputLabel-root': {
            color: '#9E9E9E',
            '&.Mui-focused': {
              color: 'primary.main',
            },
          },
        },
      },
    },
    MuiSelect: {
      styleOverrides: {
        root: {
          backgroundColor: '#FFFFFF',
        },
      },
    },
    MuiMenuItem: {
      styleOverrides: {
        root: {
          '&:hover': {
            backgroundColor: 'rgba(198, 167, 94, 0.04)',
          },
          '&.Mui-selected': {
            backgroundColor: 'rgba(198, 167, 94, 0.08)',
            '&:hover': {
              backgroundColor: 'rgba(198, 167, 94, 0.12)',
            },
          },
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          backgroundColor: 'rgba(158, 158, 158, 0.1)',
          color: '#2D2D2D',
          '&.MuiChip-colorPrimary': {
            backgroundColor: 'primary.main',
            color: '#FFFFFF',
          },
          '&.MuiChip-colorSecondary': {
            backgroundColor: '#4A4A4A',
            color: '#FFFFFF',
          },
        },
        outlined: {
          borderColor: 'rgba(158, 158, 158, 0.3)',
          backgroundColor: 'transparent',
        },
      },
    },
    MuiBadge: {
      styleOverrides: {
        badge: {
          backgroundColor: 'primary.main',
          color: '#FFFFFF',
        },
      },
    },
    MuiAvatar: {
      styleOverrides: {
        root: {
          backgroundColor: 'rgba(158, 158, 158, 0.2)',
          color: '#4A4A4A',
        },
      },
    },
    MuiListItem: {
      styleOverrides: {
        root: {
          '&.Mui-selected': {
            backgroundColor: 'rgba(198, 167, 94, 0.08)',
            '&:hover': {
              backgroundColor: 'rgba(198, 167, 94, 0.12)',
            },
          },
        },
      },
    },
    MuiTabs: {
      styleOverrides: {
        indicator: {
          backgroundColor: 'primary.main',
        },
      },
    },
    MuiTab: {
      styleOverrides: {
        root: {
          '&.Mui-selected': {
            color: 'primary.main',
          },
        },
      },
    },
    MuiSwitch: {
      styleOverrides: {
        switchBase: {
          color: '#9E9E9E',
          '&.Mui-checked': {
            color: 'primary.main',
          },
          '&.Mui-checked + .MuiSwitch-track': {
            backgroundColor: 'rgba(198, 167, 94, 0.5)',
          },
        },
        track: {
          backgroundColor: 'rgba(0,0,0,0.1)',
        },
      },
    },
    MuiLinearProgress: {
      styleOverrides: {
        root: {
          backgroundColor: 'rgba(158, 158, 158, 0.1)',
        },
        bar: {
          backgroundColor: 'primary.main',
        },
      },
    },
    MuiTableHead: {
      styleOverrides: {
        root: {
          backgroundColor: '#F5F5F5',
        },
      },
    },
    MuiTableCell: {
      styleOverrides: {
        root: {
          borderBottom: '1px solid rgba(158, 158, 158, 0.1)',
        },
        head: {
          fontWeight: 600,
          color: '#2D2D2D',
        },
      },
    },
    MuiTooltip: {
      styleOverrides: {
        tooltip: {
          backgroundColor: '#4A4A4A',
          color: '#FFFFFF',
          fontSize: '0.75rem',
        },
      },
    },
    MuiAlert: {
      styleOverrides: {
        standardSuccess: {
          backgroundColor: 'rgba(198, 167, 94, 0.1)',
          color: '#2D2D2D',
        },
      },
    },
  },
});

export default theme;